import { createTheme, ThemeProvider } from "@mui/material";
import { elGR as coreGR, enUS as coreEn } from "@mui/material/locale";
import {
  elGR as DataGridGreek,
  enUS as DataGridEnglish,
} from "@mui/x-data-grid/locales";
import {
  deDE,
  elGR as pickersGR,
  enUS as pickersEn,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import * as Sentry from "@sentry/react";
import el from "date-fns/locale/el";
import en from "date-fns/locale/en-US";
import { I18nextProvider, useTranslation } from "react-i18next";
import { QueryClientProvider } from "react-query";
import { RouterProvider } from "react-router-dom";
import { queryClient } from "src/shared/services/QeuryClient/reactQueryClient";
import "src/shared/services/languages/i18n";
import { router } from "./routes";
import { SessionTimerProvider } from "./shared/context/sessionContext";
import { AuthProvider } from "./shared/context/useAuth";
import MySnackbarProvider from "./shared/services/SnackBarProvider/MySnackbarProvider";
import { configureHttpClient } from "./shared/services/httpClient";
import GlobalMessageProvider from "./shared/context/globalMessage";
import { Helmet, HelmetProvider } from "react-helmet-async";
import FirebaseInitialize from "./firebase/features/FirebaseInitialize";
import "@mui/material/Button";
import "@mui/material/Chip";
// import i18n from "src/shared/services/languages/i18n";
const ConfigurationContextComponent = () => {
  configureHttpClient();
  return <></>;
};
const muiLanguagePacks = {
  el: [DataGridGreek, pickersGR, coreGR],
  en: [DataGridEnglish, pickersEn, coreEn],
};
const muiDatePickersLangs = {
  el: el,
  en: en,
};
const muiLocaleTextLangs = {
  el: {
    ...pickersEn.components.MuiLocalizationProvider.defaultProps.localeText,
    fieldDayPlaceholder: () => "ΗΗ",
    fieldMonthPlaceholder: () => "ΜΜ",
    fieldYearPlaceholder: (params) => "Ε".repeat(params.digitAmount),
  },
  en: pickersEn.components.MuiLocalizationProvider.defaultProps.localeText,
  de: deDE.components.MuiLocalizationProvider.defaultProps.localeText,
};
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    warningDark: true;
    darkPrimary: true;
    purple: true;
    lightPrimary: true;
  }
}

function Shell() {
  const { i18n, t } = useTranslation();
  const theme = createTheme(
    {
      typography: {
        button: {
          textTransform: "none",
        },
      },
      palette: {
        primary: {
          main: "#003476",
        },
        darkPrimary: {
          main: "#1a145d",
          contrastText: "#fff",
        },
        purple: {
          main: "#9a47af",
          contrastText: "#fff",
        },
        warningDark: {
          main: "#88441e",
          dark: "#66371c",
          contrastText: "#fff",
        },
        lightPrimary: {
          main: "#003FB4",
          contrastText: "#fff",
        },
      },
      components: {
        MuiTextField: {
          styleOverrides: {
            root: {
              "&.Mui-disabled, &.MuiInputBase-readOnly": {
                backgroundColor: "#E8EBEE",
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              "&.Mui-disabled, &.MuiInputBase-readOnly": {
                backgroundColor: "#E8EBEE",
                "& .MuiInputBase-input": {
                  WebkitTextFillColor: "#000000",
                  color: "#000000",
                },
              },
            },
          },
        },
      },
    },
    ...muiLanguagePacks[i18n.language ?? "el"],
  );
  if (import.meta.env.VITE_FIREBASE_ACTIVE === "true") {
    FirebaseInitialize();
  }
  return (
    <>
      <HelmetProvider>
        <SessionTimerProvider>
          <GlobalMessageProvider>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={muiDatePickersLangs[i18n.language]}
                  localeText={muiLocaleTextLangs[i18n.language]}
                >
                  <I18nextProvider i18n={i18n}>
                    <ThemeProvider theme={theme}>
                      <MySnackbarProvider>
                        <Helmet>
                          <title>
                            {t("Γ.Ε.ΜΗ. Υπηρεσίες προς Επιχειρήσεις")}
                          </title>
                          <meta
                            name="description"
                            content={t(
                              "Προσωποποιημένες Υπηρεσίες ΓΕΜΗ προς Επιχειρήσεις",
                            )}
                          />
                        </Helmet>
                        <ConfigurationContextComponent />
                        <RouterProvider router={router()} />
                      </MySnackbarProvider>
                    </ThemeProvider>
                  </I18nextProvider>
                </LocalizationProvider>
              </AuthProvider>
            </QueryClientProvider>
          </GlobalMessageProvider>
        </SessionTimerProvider>
      </HelmetProvider>
    </>
  );
}

export default Sentry.withProfiler(Shell);
